import React, {useLayoutEffect, useRef} from 'react';
import {Outlet, useParams} from 'react-router-dom';
import {Box} from '@mui/material';
import CampaignTabs from '@/views/campaigns/components/campaign-tabs';
import useCampaignTabsV2 from '@/views/campaigns/hooks/use-campaign-tabs-v2';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './CampaignV2.module.scss';
import CampaignCostSummary from './components/campaign-cost-summary';

const CAMPAIGN_TABS_HEIGHT_CSS_VAR = '--campaign-tabs-height';

function CampaignV2() {
  const {campaignId} = useParams<{campaignId: string}>();
  const brandId = useBrandId();
  const {campaign} = useCampaignQuery({campaignId});
  const {allowEditCreatorBid} = useAgencyQuery({brandId: brandId ?? ''});
  const {tabs} = useCampaignTabsV2({
    campaignId,
    hasSocialNetwork: !!campaign?.socialNetwork,
  });

  // Publish the actual rendered height of the campaign tabs row as a CSS
  // variable so floating UI anchored below it (e.g. the creator preview
  // side panel) can stay flush with the tabs bottom without hardcoding a
  // magic px value. ResizeObserver keeps it in sync if anything that
  // changes the tab height (font size, density, etc.) updates later.
  const tabsRowRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tabsRow = tabsRowRef.current;
    if (!tabsRow) return undefined;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        CAMPAIGN_TABS_HEIGHT_CSS_VAR,
        `${tabsRow.offsetHeight}px`
      );
    };
    publishHeight();

    const observer = new ResizeObserver(publishHeight);
    observer.observe(tabsRow);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(
        CAMPAIGN_TABS_HEIGHT_CSS_VAR
      );
    };
  }, []);

  return (
    <Box className={styles.container}>
      <Box ref={tabsRowRef} className={styles.tabsRow}>
        <CampaignTabs tabs={tabs} />
        <CampaignCostSummary
          campaign={campaign}
          allowEditCreatorBid={allowEditCreatorBid}
        />
      </Box>
      <Box className={styles.body}>
        <Outlet />
      </Box>
    </Box>
  );
}

export default CampaignV2;
